import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api.js';
import { PageTabs, chargebacksTabs } from '../components/PageTabs.js';
import {
  Button,
  Card,
  EmptyState,
  Field,
  ICONS,
  Loading,
  SortHeader,
  inputClass,
  useSort,
} from '../components/ui.js';

type DisputeStatus = 'open' | 'in_progress' | 'recovered' | 'written_off';

interface Dispute {
  id: string;
  title: string;
  notes: string | null;
  amountCents: number;
  currency: string;
  status: DisputeStatus;
  source: string | null;
  assignee: string | null;
  carrierId: string | null;
  memberRefId: string | null;
  policyRefId: string | null;
  periodYear: number | null;
  periodMonth: number | null;
  createdAt: number | string;
  resolvedAt: number | string | null;
}

const SOURCE_LABEL: Record<string, string> = {
  chargeback: 'chargeback',
  commission_owed: 'owed',
  manual: 'manual',
};

const STATUS_META: Record<DisputeStatus, { label: string; cls: string }> = {
  open: {
    label: 'Open',
    cls: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
  },
  in_progress: {
    label: 'In progress',
    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  },
  recovered: {
    label: 'Recovered',
    cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300',
  },
  written_off: {
    label: 'Written off',
    cls: 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300',
  },
};

/** Transitions the server allows (mirrors apps/api/src/routes/disputes.ts). */
const NEXT: Record<DisputeStatus, DisputeStatus[]> = {
  open: ['in_progress', 'recovered', 'written_off'],
  in_progress: ['recovered', 'written_off', 'open'],
  recovered: [],
  written_off: [],
};

// Accounting sign so a negative (a disputed clawback/chargeback) renders as
// ($500.00) rather than -$500.00.
const money = (cents: number, ccy: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: ccy || 'USD',
    currencySign: 'accounting',
  }).format(cents / 100);

const periodLabel = (y: number | null, m: number | null) =>
  y && m ? `${String(m).padStart(2, '0')}/${y}` : '—';

const isEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

/**
 * Dispute & recovery worklist — ENTERPRISE, app-only. Turns a commission shortfall
 * or chargeback into a tracked recovery item (open → in_progress → recovered /
 * written off). Gated by the `disputes` entitlement; the page also self-guards so a
 * direct visit shows a not-enabled state rather than firing 403s. Calls the
 * /v1/disputes routes via direct fetch (deliberately off the public SDK).
 */
export function Disputes() {
  const { baseUrl, token, entitlements } = useAuth();
  const url = `${baseUrl.replace(/\/$/, '')}/disputes`;
  const authHeaders = { authorization: `Bearer ${token}`, 'content-type': 'application/json' };

  const [items, setItems] = useState<Dispute[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Inline "assign to an email" on a single dispute row.
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [assignEmail, setAssignEmail] = useState('');

  // Sortable columns (derive a chronological period key; spread keeps all fields).
  const sortRows = useMemo(
    () => items.map((d) => ({ ...d, periodOrd: (d.periodYear ?? 0) * 12 + (d.periodMonth ?? 0) })),
    [items],
  );
  const { sorted, sortKey, sortDir, toggle } = useSort(sortRows, { key: 'periodOrd', dir: 'desc' });

  const load = () =>
    fetch(url, { headers: authHeaders })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((b) => setItems(b.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));

  // biome-ignore lint/correctness/useExhaustiveDependencies: load once on mount
  useEffect(() => {
    if (token && entitlements.disputes) load();
    else setLoading(false);
  }, [token]);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const amountCents = amount.trim() ? Math.round(Number(amount) * 100) : 0;
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          title: title.trim(),
          amountCents,
          assignee: assignee.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.title ?? `Failed (${res.status})`);
      }
      setTitle('');
      setAmount('');
      setAssignee('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create');
    } finally {
      setBusy(false);
    }
  };

  const advance = async (id: string, status: DisputeStatus) => {
    await fetch(`${url}/${id}`, {
      method: 'PATCH',
      headers: authHeaders,
      body: JSON.stringify({ status }),
    });
    await load();
  };

  const remove = async (id: string) => {
    await fetch(`${url}/${id}`, { method: 'DELETE', headers: authHeaders });
    await load();
  };

  // Assign an unassigned dispute to a teammate's email (e.g. a chargeback dispute
  // that was created in one click and needs an owner).
  const assign = async (id: string) => {
    const email = assignEmail.trim();
    if (!isEmail(email)) return;
    await fetch(`${url}/${id}`, {
      method: 'PATCH',
      headers: authHeaders,
      body: JSON.stringify({ assignee: email }),
    });
    setAssigningId(null);
    setAssignEmail('');
    await load();
  };

  if (!entitlements.disputes) {
    return (
      <div className="mx-auto max-w-3xl">
        <Card>
          <EmptyState
            icon={ICONS.list}
            title="Dispute & recovery workflow"
            hint="This enterprise feature isn't enabled for your account. Contact CommissionSight to turn it on."
          />
        </Card>
      </div>
    );
  }

  const open = items.filter((d) => d.status === 'open' || d.status === 'in_progress');
  const recoveredCents = items
    .filter((d) => d.status === 'recovered')
    .reduce((s, d) => s + d.amountCents, 0);
  const outstandingCents = open.reduce((s, d) => s + d.amountCents, 0);

  return (
    <div className="space-y-6">
      <PageTabs tabs={chargebacksTabs(entitlements)} />
      <div>
        <h1 className="text-xl font-semibold">Disputes &amp; recovery</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Track underpaid or missing commission through to resolution. {open.length} open ·{' '}
          {money(outstandingCents, 'USD')} outstanding · {money(recoveredCents, 'USD')} recovered.
        </p>
      </div>

      <Card>
        <h2 className="mb-1 font-semibold">Open a recovery item</h2>
        <p className="mb-3 text-sm text-neutral-500">
          Create a custom dispute for <strong>any amount</strong> — a period&apos;s owed rollup, a
          flagged shortfall, or an incorrect clawback (enter a negative amount). Assign it to a
          teammate now, or add an owner later with the <strong>Assign</strong> button on any row. To
          dispute a chargeback, use the one-click flag on the{' '}
          <Link
            to="/chargebacks"
            className="text-emerald-600 hover:underline dark:text-emerald-400"
          >
            Chargebacks
          </Link>{' '}
          page.
        </p>
        <div className="grid gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <Field label="What's owed">
              <input
                className={inputClass}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Underpaid March Aetna commission"
              />
            </Field>
          </div>
          <Field label="Amount (optional)">
            <input
              className={inputClass}
              type="number"
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00 (negative for a clawback)"
            />
          </Field>
          <Field label="Assignee (optional)">
            <input
              className={inputClass}
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              placeholder="rep@agency.com"
            />
          </Field>
        </div>
        {error && <p className="mt-2 text-sm text-rose-600">{error}</p>}
        <div className="mt-3">
          <Button onClick={create} loading={busy} disabled={!title.trim()}>
            Add dispute
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden p-0">
        {loading && items.length === 0 ? (
          <Loading label="Loading disputes…" />
        ) : items.length === 0 ? (
          <EmptyState
            icon={ICONS.list}
            title="No disputes yet"
            hint="Flag a commission shortfall above to start tracking a recovery."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-200 text-left text-xs uppercase tracking-wide text-neutral-500 dark:border-neutral-800">
              <tr>
                {(() => {
                  const sp = { sortKey, sortDir, onSort: toggle };
                  return (
                    <>
                      <SortHeader label="Item" k="title" {...sp} className="px-5 py-3" />
                      <SortHeader label="Amount" k="amountCents" {...sp} className="px-5 py-3" />
                      <SortHeader label="Period" k="periodOrd" {...sp} className="px-5 py-3" />
                      <SortHeader label="Assignee" k="assignee" {...sp} className="px-5 py-3" />
                      <SortHeader label="Status" k="status" {...sp} className="px-5 py-3" />
                      <th className="px-5 py-3 font-medium text-right">Actions</th>
                    </>
                  );
                })()}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {sorted.map((d) => (
                <tr key={d.id}>
                  <td className="px-5 py-3">
                    <div className="font-medium">{d.title}</div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-neutral-500">
                      {d.source && d.source !== 'manual' && (
                        <span className="rounded bg-neutral-100 px-1.5 py-0.5 font-medium text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
                          {SOURCE_LABEL[d.source] ?? d.source}
                        </span>
                      )}
                      {d.memberRefId && (
                        <Link
                          to={`/members/${d.memberRefId}`}
                          className="text-emerald-600 hover:underline dark:text-emerald-400"
                          title="View this member's journey"
                        >
                          member ↗
                        </Link>
                      )}
                      {d.notes && <span>{d.notes}</span>}
                    </div>
                  </td>
                  <td
                    className={`px-5 py-3 tabular-nums ${d.amountCents < 0 ? 'text-rose-600 dark:text-rose-400' : ''}`}
                  >
                    {money(d.amountCents, d.currency)}
                  </td>
                  <td className="px-5 py-3">{periodLabel(d.periodYear, d.periodMonth)}</td>
                  <td className="px-5 py-3">
                    {d.assignee ? (
                      d.assignee
                    ) : assigningId === d.id ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          className={`${inputClass} h-8 w-44`}
                          type="email"
                          // Focus on mount when the inline editor appears (avoids the
                          // a11y-flagged autoFocus attribute while keeping the UX).
                          ref={(el) => el?.focus()}
                          placeholder="rep@agency.com"
                          value={assignEmail}
                          onChange={(e) => setAssignEmail(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && assign(d.id)}
                        />
                        <Button
                          size="sm"
                          onClick={() => assign(d.id)}
                          disabled={!isEmail(assignEmail)}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setAssigningId(null);
                            setAssignEmail('');
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setAssigningId(d.id);
                          setAssignEmail('');
                        }}
                      >
                        Assign
                      </Button>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_META[d.status].cls}`}
                    >
                      {STATUS_META[d.status].label}
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {NEXT[d.status].map((s) => (
                        <Button key={s} variant="ghost" size="sm" onClick={() => advance(d.id, s)}>
                          {STATUS_META[s].label}
                        </Button>
                      ))}
                      <Button variant="ghost" size="sm" onClick={() => remove(d.id)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
